import { useContext, useEffect, useRef } from "react";

import {
  clearPersistedAttributionAtAuthBoundary,
  getLastTouchCapturedByThisPageLoad,
  persistAttributionTouch,
} from "@/lib/analytics/attributionCookies";
import {
  consumeObservedMarketingIdentityBoundary,
  getMarketingSessionId,
  peekMarketingSessionId,
  rotateMarketingSessionId,
} from "@/lib/analytics/marketingSession";
import { observeCurrentWebAnalyticsConsent } from "@/lib/analytics/marketingObservations";
import { reconcileConsentFromSharedCookie } from "@/lib/consent/cookiebar";
import { AuthContext } from "@/shared/context/auth-context";

const AUTHENTICATED_OWNER_BROWSER_MARKER = "ee_marketing_had_authenticated_owner";

const hadAuthenticatedOwnerInThisBrowser = (): boolean => {
  if (typeof window === "undefined") return false;
  try {
    return window.localStorage.getItem(AUTHENTICATED_OWNER_BROWSER_MARKER) === "1";
  } catch {
    return false;
  }
};

const rememberAuthenticatedOwnerInThisBrowser = (isAuthenticated: boolean): void => {
  if (typeof window === "undefined") return;
  try {
    if (isAuthenticated) {
      window.localStorage.setItem(AUTHENTICATED_OWNER_BROWSER_MARKER, "1");
    } else {
      window.localStorage.removeItem(AUTHENTICATED_OWNER_BROWSER_MARKER);
    }
  } catch {
    // Storage can be blocked; the server still refuses cross-owner session reuse.
  }
};

/**
 * The sole bridge between authenticated application state and browser analytics. Business auth
 * code exposes the verified session; this component handles journey rotation across ownership
 * boundaries without adding analytics fields to auth inputs or forcing browser reloads.
 */
export function AuthAnalyticsBridge(): null {
  const { session, isFetching, isError } = useContext(AuthContext);
  const lastAuthenticatedOwnerId = useRef<string | null>(null);
  // Rendering must not adopt a cross-tab cookie or notify external-store subscribers. The
  // authoritative getter runs below in the effect, where identity-boundary side effects belong.
  const lastBrowserMarketingSessionId = useRef(peekMarketingSessionId());

  useEffect(() => {
    const user = session?.user;
    const ownerUserId = user?.spoofingId ?? user?.id;

    if (isFetching || isError) return;

    const previousOwnerUserId = lastAuthenticatedOwnerId.current;
    const recoveredExpiredAuthBoundary =
      previousOwnerUserId === null && ownerUserId === undefined && hadAuthenticatedOwnerInThisBrowser();
    const knownOwnerBoundary = Boolean(previousOwnerUserId && previousOwnerUserId !== ownerUserId);
    const ownerBoundary = knownOwnerBoundary || recoveredExpiredAuthBoundary;
    const browserMarketingSessionId = getMarketingSessionId();
    const observedServerBoundary = consumeObservedMarketingIdentityBoundary();
    const browserJourneyAlreadyReplaced =
      observedServerBoundary.journeyReplaced || browserMarketingSessionId !== lastBrowserMarketingSessionId.current;

    if (ownerBoundary && !browserJourneyAlreadyReplaced) {
      // An expired session is only noticed once the session check settles, after the landing page
      // captured its attribution. That touch is the current visitor's, so it is persisted again
      // after the rotation has cleared everything the previous owner left behind.
      const landingTouch = recoveredExpiredAuthBoundary ? getLastTouchCapturedByThisPageLoad() : undefined;
      clearPersistedAttributionAtAuthBoundary();
      const rotatedMarketingSessionId = rotateMarketingSessionId();
      if (landingTouch) persistAttributionTouch(landingTouch, landingTouch);
      lastBrowserMarketingSessionId.current = rotatedMarketingSessionId;
      reconcileConsentFromSharedCookie();
      observeCurrentWebAnalyticsConsent();
    } else if (ownerBoundary || browserJourneyAlreadyReplaced) {
      lastBrowserMarketingSessionId.current = browserMarketingSessionId;
      clearPersistedAttributionAtAuthBoundary();
      reconcileConsentFromSharedCookie();
      observeCurrentWebAnalyticsConsent();
    } else {
      lastBrowserMarketingSessionId.current = browserMarketingSessionId;
    }

    lastAuthenticatedOwnerId.current = ownerUserId ?? null;
    rememberAuthenticatedOwnerInThisBrowser(Boolean(ownerUserId));
  }, [isError, isFetching, session]);

  return null;
}
